import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { BadgeCheck, Mail, IdCard, Clock, XCircle } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ThiriMascot } from "@/components/ThiriMascot";

export default function Verify() {
  const { user, refreshUser, logout } = useAuth();
  const navigate = useNavigate();
  const [institutions, setInstitutions] = useState([]);
  const [instChoice, setInstChoice] = useState("");
  const [phone, setPhone] = useState("");
  const [otpEmail, setOtpEmail] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otpCode, setOtpCode] = useState("");
  const [idForm, setIdForm] = useState({ name_on_id: "", id_number: "", image_base64: "" });
  const [fileName, setFileName] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user?.institution_id) api.get("/institutions").then(({ data }) => setInstitutions(data)).catch(() => {});
  }, [user?.institution_id]);

  useEffect(() => {
    if (user && user.verification_status === "verified" && user.role === "member") navigate("/home");
  }, [user, navigate]);

  if (!user || user === false) return null;

  const selectInstitution = async () => {
    if (!instChoice) return toast.error("Select your institution");
    try {
      await api.post("/auth/select-institution", { institution_id: instChoice, phone: phone || undefined });
      await refreshUser();
      toast.success("Institution selected");
    } catch (e) { toast.error(apiError(e)); }
  };

  const sendOtp = async () => {
    if (!otpEmail.trim()) return toast.error("Enter your institution email");
    setBusy(true);
    try {
      const { data } = await api.post("/verification/send-otp", { institution_email: otpEmail });
      setOtpSent(true);
      toast.success(data.email_sent
        ? `Code sent to ${data.to}. Check your inbox.`
        : "Code generated. Email delivery isn't configured yet — contact your admin for the code.");
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const submitOtp = async () => {
    if (otpCode.trim().length !== 6) return toast.error("Enter the 6-digit code");
    setBusy(true);
    try {
      await api.post("/verification/verify-otp", { code: otpCode.trim() });
      toast.success("Verified! Welcome to THIRI.");
      await refreshUser();
      navigate("/home");
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) return toast.error("Image must be under 5MB");
    setFileName(f.name);
    const reader = new FileReader();
    reader.onload = () => setIdForm((s) => ({ ...s, image_base64: String(reader.result).split(",")[1] }));
    reader.readAsDataURL(f);
  };

  const submitId = async (e) => {
    e.preventDefault();
    if (!idForm.image_base64) return toast.error("Upload a photo of your institution ID");
    setBusy(true);
    try {
      const { data } = await api.post("/verification/id-upload", idForm);
      if (data.status === "verified") {
        toast.success("ID verified! Welcome to THIRI.");
        await refreshUser();
        navigate("/home");
      } else if (data.status === "manual_review") {
        toast.info("Sent for manual review by an admin.");
        await refreshUser();
      } else {
        toast.error("We couldn't verify that image. Try a clearer photo of your ID.");
        await refreshUser();
      }
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  const status = user.verification_status;

  return (
    <div className="min-h-screen max-w-lg mx-auto px-4 py-10">
      <span className="font-display font-black text-2xl text-primary">THIRI</span>
      <h1 className="font-display font-bold text-2xl mt-6">Verify your institution</h1>
      <p className="text-sm text-muted-foreground mt-1">
        THIRI is verified-only. Prove you belong to your institution to unlock rides and the campus feed.
      </p>

      <div className="mt-6">
        <ThiriMascot state="safety" message="Your ID document is stored securely and never shown to other members. Only admins can review it." />
      </div>

      {!user.institution_id ? (
        <div className="t-card p-6 mt-6 space-y-4" data-testid="institution-select-card">
          <h2 className="font-display font-bold">Select your institution</h2>
          <Select value={instChoice} onValueChange={setInstChoice}>
            <SelectTrigger data-testid="verify-institution-select" className="rounded-2xl">
              <SelectValue placeholder="Choose institution" />
            </SelectTrigger>
            <SelectContent>
              {institutions.map((i) => <SelectItem key={i.id} value={i.id}>{i.name} · {i.type}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input data-testid="verify-phone-input" placeholder="Phone number (optional)" value={phone} onChange={(e) => setPhone(e.target.value)} className="rounded-2xl" />
          <Button data-testid="verify-institution-btn" onClick={selectInstitution} className="w-full rounded-full font-bold">Continue</Button>
        </div>
      ) : (
        <>
          {status === "manual_review" && (
            <div className="t-card p-6 mt-6 flex items-center gap-3" data-testid="manual-review-banner">
              <Clock className="w-6 h-6 text-primary shrink-0" />
              <div>
                <p className="font-bold">Under manual review</p>
                <p className="text-sm text-muted-foreground">An admin is reviewing your ID. You'll get a notification once it's done.</p>
              </div>
            </div>
          )}
          {status === "rejected" && (
            <div className="t-card p-6 mt-6 flex items-center gap-3" data-testid="rejected-banner">
              <XCircle className="w-6 h-6 text-destructive shrink-0" />
              <div>
                <p className="font-bold">Verification rejected</p>
                <p className="text-sm text-muted-foreground">Try again with a clearer photo of your official institution ID.</p>
              </div>
            </div>
          )}

          <div className="t-card p-6 mt-6 space-y-3">
            <div className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-primary" />
              <h2 className="font-display font-bold">Option A · Institution email OTP</h2>
            </div>
            <p className="text-sm text-muted-foreground">
              Enter your official institution email (e.g. you@hmritm.ac.in). We'll send a 6-digit code to prove it's yours.
            </p>
            <Input data-testid="otp-email-input" type="email" placeholder="you@hmritm.ac.in"
              value={otpEmail} onChange={(e) => setOtpEmail(e.target.value)} className="rounded-2xl" />
            {otpSent && (
              <Input data-testid="otp-code-input" inputMode="numeric" maxLength={6} placeholder="6-digit code"
                value={otpCode} onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ""))}
                className="rounded-2xl text-center text-lg tracking-[0.5em] font-bold" />
            )}
            <div className="flex gap-2">
              <Button data-testid="send-otp-btn" disabled={busy} onClick={sendOtp} variant={otpSent ? "outline" : "default"} className="rounded-full font-bold flex-1">
                <BadgeCheck className="w-4 h-4 mr-2" /> {otpSent ? "Resend code" : "Send code"}
              </Button>
              {otpSent && (
                <Button data-testid="verify-otp-btn" disabled={busy || otpCode.length !== 6} onClick={submitOtp} className="rounded-full font-bold flex-1">
                  Verify code
                </Button>
              )}
            </div>
          </div>

          <form onSubmit={submitId} className="t-card p-6 mt-6 space-y-4">
            <div className="flex items-center gap-2">
              <IdCard className="w-5 h-5 text-primary" />
              <h2 className="font-display font-bold">Option B · College ID or document</h2>
            </div>
            <p className="text-sm text-muted-foreground">
              No college email? Upload your student ID card, admission letter, fee receipt or bonafide certificate — AI verifies it against your institution.
            </p>
            <Input data-testid="id-name-input" required placeholder="Name exactly as on ID / document"
              value={idForm.name_on_id} onChange={(e) => setIdForm({ ...idForm, name_on_id: e.target.value })} className="rounded-2xl" />
            <Input data-testid="id-number-input" placeholder="Student / enrollment number (optional)"
              value={idForm.id_number} onChange={(e) => setIdForm({ ...idForm, id_number: e.target.value })} className="rounded-2xl" />
            <label className="block">
              <span className="text-sm font-semibold">ID / document photo (JPEG/PNG, max 5MB)</span>
              <input data-testid="id-file-input" type="file" accept="image/jpeg,image/png,image/webp" onChange={onFile}
                className="mt-1 block w-full text-sm file:mr-3 file:rounded-full file:border-0 file:bg-primary/10 file:px-4 file:py-2 file:text-sm file:font-bold file:text-primary" />
              {fileName && <span className="text-xs text-muted-foreground">{fileName}</span>}
            </label>
            <Button data-testid="id-upload-submit-btn" disabled={busy} type="submit" className="w-full rounded-full font-bold">
              {busy ? "Checking your ID…" : "Submit for verification"}
            </Button>
          </form>
        </>
      )}

      <button data-testid="verify-logout-btn" onClick={async () => { await logout(); navigate("/"); }}
        className="mt-6 text-sm text-muted-foreground hover:text-foreground transition-colors duration-200">
        Log out
      </button>
    </div>
  );
}
